import { Badge, Box, Button, Flex, IconButton, Select, Text, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowBackIcon } from "@chakra-ui/icons";
import PageLayout from "components/layout/PageLayout";
import AttachmentsChips from "../../customer/support/components/messages/AttachmentsChips";
import ChatHeader from "../../customer/support/components/messages/ChatHeader";
import MessageComposer from "../../customer/support/components/messages/MessageComposer";
import MessagesList from "../../customer/support/components/messages/MessagesList";
import { supportApi, useMessagesTab } from "../../customer/support/hooks";
import { allUsersSelector } from "store/user/selector";
import { cacheUser } from "store/support/reducer";
import { cachedUserByIdSelector } from "store/support/selector";
import { axiosService } from "network/axios";
import { useSupportUnreadContext } from "contexts/SupportUnreadContext";

export default function SupportChat() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { chatId } = useParams();
  const location = useLocation();
  const { refetch: refetchUnreadCount } = useSupportUnreadContext();

  const [isClosing, setIsClosing] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState<string>("");

  const allUsers = useSelector(allUsersSelector) as any;
  const managers = useMemo(() => allUsers?.projectManagers ?? [], [allUsers]);

  const navChat = (location as any)?.state?.chat ?? null;

  const {
    selectedChatId,
    setSelectedChatId,
    selectedChat,
    selectedChatTitle,
    selectedChatDisplayDate,
    selectedChatStatus,
    closeChat,
    messages,
    isMessagesLoading,
    onRetrySend,
    scrollToBottomKey,
    setIsAtBottom,
    onLoadOlder,
    isLoadingOlder,
    hasMoreOlder,
    currentCustomerId,
    draft,
    setDraft,
    attachedFiles,
    isUploading,
    openFilePicker,
    removeFile,
    isSending,
    onSend,
  } = useMessagesTab({ autoSelectFirstChat: false, forceFetchOnChatChange: true });

  useEffect(() => {
    if (chatId != null) {
      setSelectedChatId(chatId as any);
      // Refresh unread count when opening a chat
      refetchUnreadCount();
    }
  }, [chatId, setSelectedChatId, refetchUnreadCount]);

  const isClosed = selectedChatStatus === "Closed" || (!selectedChatStatus && navChat?.isClosed);

  const onCloseTopic = useCallback(async () => {
    if (selectedChatId == null) return;
    if (isClosed) return;
    if (isClosing) return;

    setIsClosing(true);
    try {
      const resp: any = await closeChat(selectedChatId);
      const status = Number(resp?.status);
      if (status === 200 || status === 201) {
        // Determine current layout from pathname
        const currentLayout = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
        navigate(`${currentLayout}/support`);
      }
    } finally {
      setIsClosing(false);
    }
  }, [closeChat, isClosed, isClosing, navigate, selectedChatId, location.pathname]);

  const headerTitle = selectedChatTitle || navChat?.title || "Support request";
  const headerDisplayDate = selectedChatDisplayDate || navChat?.displayDate || "";
  const headerStatus = selectedChatStatus || (navChat?.isClosed ? "Closed" : "Open") || "Open";

  const orderId =
    (selectedChat as any)?.order_id ??
    (selectedChat as any)?.orderId ??
    (selectedChat as any)?.order?.order_id ??
    (selectedChat as any)?.order?.id ??
    navChat?.orderId ??
    null;

  const currentAssignedManagerId =
    (selectedChat as any)?.assigned_manager_id ??
    navChat?.assigned_manager_id ??
    null;

  const createdByUserId =
    (selectedChat as any)?.created_by ??
    navChat?.created_by ??
    null;

  const cachedCustomerSelector = useMemo(
    () => cachedUserByIdSelector(createdByUserId),
    [createdByUserId]
  );
  const cachedCustomer = useSelector(cachedCustomerSelector as any) as any;

  useEffect(() => {
    if (createdByUserId == null) return;
    if (cachedCustomer != null) return;
    let cancelled = false;
    axiosService
      .get(`/user/get/${createdByUserId}`)
      .then((res: any) => {
        if (cancelled) return;
        const user = res?.data?.user ?? res?.data;
        if (user) {
          dispatch(cacheUser({ ...user, user_id: createdByUserId }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cachedCustomer, createdByUserId, dispatch]);

  const customerDisplayName = useMemo(() => {
    if (!cachedCustomer) return null;
    const name = `${cachedCustomer.first_name ?? ""} ${cachedCustomer.last_name ?? ""}`.trim();
    return name || cachedCustomer.email || null;
  }, [cachedCustomer]);

  const managerOptions = useMemo(() => {
    return (managers ?? []).map((m: any) => ({
      user_id: m.user_id,
      label: `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || `ID ${m.user_id}`,
    }));
  }, [managers]);

  useEffect(() => {
    if (currentAssignedManagerId != null) {
      setSelectedManagerId(String(currentAssignedManagerId));
    }
  }, [currentAssignedManagerId]);

  const onAssignManager = useCallback(async () => {
    if (!selectedChatId || !selectedManagerId) return;
    if (isAssigning) return;
    setIsAssigning(true);
    try {
      await supportApi.assignManager(selectedChatId, Number(selectedManagerId));
      toast({
        title: "Manager assigned",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
        variant: "solid",
      });
    } catch (e: any) {
      toast({
        title: "Failed to assign manager",
        description: e?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
        variant: "solid",
      });
    } finally {
      setIsAssigning(false);
    }
  }, [isAssigning, selectedChatId, selectedManagerId, toast]);

  return (
    <PageLayout topContent={<Box h={{ base: "60px", md: "80px" }} />}>
      <Box
        w="100%"
        minH={{ base: "auto", md: "741px" }}
        h={{ base: "auto", md: "741px" }}
        p={{ base: "16px", md: "24px" }}
        bg="#F5F7FA"
        border="1px solid #E6ECF3"
        borderRadius="12px"
        overflow="hidden"
      >
        <Box
          bg="#FFFFFF"
          border="1px solid #E6ECF3"
          borderRadius="12px"
          h="100%"
          maxH="100%"
          p="24px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Flex justify="space-between" align="center" mb="12px">
            <IconButton
              aria-label="Back"
              icon={<ArrowBackIcon />}
              variant="ghost"
              display={{ base: "inline-flex", md: "none" }}
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              _focusVisible={{ boxShadow: "none" }}
              onClick={() => {
                const currentLayout = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
                navigate(`${currentLayout}/support`);
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              p="0px"
              display={{ base: "none", md: "inline-flex" }}
              leftIcon={<ArrowBackIcon />}
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              _focusVisible={{ boxShadow: "none" }}
              onClick={() => {
                const currentLayout = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
                navigate(`${currentLayout}/support`);
              }}
            >
              Back
            </Button>
            <Badge
              h="32px"
              px="8px"
              py="7px"
              w="52px"
              borderRadius="8px"
              fontSize="14px"
              fontWeight="400"
              bg={isClosed ? "#9CA3AF" : "#BFD7FF"}
              color={isClosed ? "#FFF" : "#0B0E10"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              textTransform="none"
            >
              {headerStatus}
            </Badge>
          </Flex>

          <Box borderTop="1px solid #E6ECF3" pt="12px" />
          <Flex align="center" gap="12px" mb="12px" flexWrap="wrap">
            <Text fontSize="14px" fontWeight="500" color="#505E70" whiteSpace="nowrap">
              Assign manager:
            </Text>
            <Select
              size="sm"
              maxW="260px"
              borderRadius="8px"
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
              placeholder="Select manager"
            >
              {managerOptions.map((m: any) => (
                <option key={m.user_id} value={String(m.user_id)}>
                  {m.label}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              bgColor="#3A7BFF"
              color="#FFFFFF"
              borderRadius="25px"
              border="1px solid #2C63E5"
              px="24px"
              _hover={{ bgColor: "#FFFFFF", color: "#2C63E5" }}
              _active={{ bgColor: "#FFFFFF", color: "#2C63E5" }}
              _loading={{ pointerEvents: "none" }}
              isLoading={isAssigning}
              isDisabled={
                isAssigning ||
                !selectedManagerId ||
                selectedManagerId === String(currentAssignedManagerId)
              }
              onClick={onAssignManager}
            >
              Assign
            </Button>
          </Flex>



          <ChatHeader
            title={headerTitle}
            displayDate={headerDisplayDate}
            status={headerStatus}
            orderId={orderId != null && Number(orderId) !== 0 ? String(orderId) : undefined}
            createdByUserId={createdByUserId}
            cachedCustomer={cachedCustomer}
            customerDisplayName={customerDisplayName}
            rightExtra={
              isClosed ? null : (
                <Button
                  size="sm"
                  onClick={onCloseTopic}
                  bg="#fff"
                  border="1px solid #3A7BFF"
                  _hover={{ bg: "#3A7BFF", color: "#fff" }}
                  color="#3A7BFF"
                  isLoading={isClosing}
                  isDisabled={isClosing || selectedChatId == null}
                >
                  Close topic
                </Button>
              )
            }
          />

          <MessagesList
            isLoading={isMessagesLoading}
            messages={messages ?? []}
            currentCustomerId={currentCustomerId}
            otherPartyLabel="Customer"
            onRetrySend={onRetrySend}
            scrollToBottomKey={scrollToBottomKey}
            onAtBottomChange={setIsAtBottom}
            onLoadOlder={onLoadOlder}
            isLoadingOlder={isLoadingOlder}
            hasMoreOlder={hasMoreOlder}
          />

          {isClosed ? null : (
            <>
              <AttachmentsChips files={attachedFiles} onRemove={removeFile} />

              <MessageComposer
                draft={draft}
                onDraftChange={setDraft}
                onAttach={openFilePicker}
                isUploading={isUploading}
                onSend={onSend}
                isSending={isSending}
              />
            </>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}
